import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DollarSign, TrendingUp, Users, Download, RefreshCw, BarChart3, Repeat,
} from "lucide-react";
import { format } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface SpPerformance {
  user_id: string;
  name: string;
  revenue: number;
  commission: number;
  netContribution: number;
  customers: number;
  repeatCustomers: number;
  repeatRate: number;
  aov: number;
  estimatedLtv: number;
  orderCount: number;
}

const SalesPerformanceReport = () => {
  const [data, setData] = useState<SpPerformance[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [ordersRes, profilesRes, settingsRes] = await Promise.all([
      supabase.from("orders").select("salesperson_id, patient_email, amount_cents, commission_rate, status, created_at").not("salesperson_id", "is", null),
      supabase.from("profiles").select("user_id, first_name, last_name"),
      supabase.from("salesperson_settings").select("user_id, commission_rate"),
    ]);

    const profileMap = new Map((profilesRes.data || []).map((p: any) => [p.user_id, p]));
    const orders = (ordersRes.data || []).filter((o: any) => !["cancelled", "refunded"].includes(o.status));

    // Group by salesperson
    const spMap = new Map<string, typeof orders>();
    orders.forEach((o: any) => {
      const list = spMap.get(o.salesperson_id) || [];
      list.push(o);
      spMap.set(o.salesperson_id, list);
    });

    const rows: SpPerformance[] = Array.from(spMap.entries()).map(([spId, ords]) => {
      const profile = profileMap.get(spId);
      const name = profile ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || spId.slice(0, 8) : spId.slice(0, 8);
      const revenue = ords.reduce((s: number, o: any) => s + o.amount_cents, 0);
      const commission = ords.reduce((s: number, o: any) => s + (o.amount_cents * o.commission_rate) / 100, 0);

      // Customer analysis
      const customerOrders = new Map<string, number>();
      ords.forEach((o: any) => {
        const key = o.patient_email || o.id;
        customerOrders.set(key, (customerOrders.get(key) || 0) + 1);
      });
      const customers = customerOrders.size;
      const repeatCustomers = Array.from(customerOrders.values()).filter(c => c > 1).length;

      const aov = ords.length > 0 ? revenue / ords.length : 0;
      // Simple LTV estimate: AOV * avg orders per customer * 12-month projection
      const avgOrdersPerCustomer = customers > 0 ? ords.length / customers : 0;
      const estimatedLtv = aov * Math.max(avgOrdersPerCustomer * 2, 1);

      return {
        user_id: spId,
        name,
        revenue,
        commission,
        netContribution: revenue - commission,
        customers,
        repeatCustomers,
        repeatRate: customers > 0 ? Math.round((repeatCustomers / customers) * 100) : 0,
        aov,
        estimatedLtv,
        orderCount: ords.length,
      };
    }).sort((a, b) => b.netContribution - a.netContribution);

    setData(rows);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = useMemo(() => ({
    revenue: data.reduce((s, d) => s + d.revenue, 0),
    commission: data.reduce((s, d) => s + d.commission, 0),
    net: data.reduce((s, d) => s + d.netContribution, 0),
    customers: data.reduce((s, d) => s + d.customers, 0),
    repeatCustomers: data.reduce((s, d) => s + d.repeatCustomers, 0),
    avgLtv: data.length > 0 ? data.reduce((s, d) => s + d.estimatedLtv, 0) / data.length : 0,
  }), [data]);

  const retentionRate = totals.customers > 0 ? Math.round((totals.repeatCustomers / totals.customers) * 100) : 0;

  const chartData = data.slice(0, 10).map(d => ({
    name: d.name.length > 10 ? d.name.slice(0, 10) + "…" : d.name,
    "Net ($)": Math.round(d.netContribution) / 100,
    "Commission ($)": Math.round(d.commission) / 100,
  }));

  const exportCSV = () => {
    const headers = ["Name", "Revenue", "Commission", "Net Contribution", "Customers", "Repeat %", "AOV", "Est. LTV", "Orders"];
    const csvRows = data.map(d => [
      d.name, (d.revenue / 100).toFixed(2), (d.commission / 100).toFixed(2),
      (d.netContribution / 100).toFixed(2), d.customers, d.repeatRate,
      (d.aov / 100).toFixed(2), (d.estimatedLtv / 100).toFixed(2), d.orderCount,
    ].join(","));
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sales-performance-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg">Sales Performance & Profitability</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1" disabled={data.length === 0}>
            <Download size={14} /> Export CSV
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={fetchData}>
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      {/* Company KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: "Total Revenue", value: fmt(totals.revenue), icon: DollarSign },
          { label: "Total Commissions", value: fmt(totals.commission), icon: DollarSign },
          { label: "Net Margin", value: fmt(totals.net), icon: TrendingUp },
          { label: "Total Customers", value: totals.customers, icon: Users },
          { label: "Retention Rate", value: `${retentionRate}%`, icon: Repeat },
          { label: "Avg Est. LTV", value: fmt(totals.avgLtv), icon: BarChart3 },
        ].map((k) => (
          <Card key={k.label} className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <k.icon size={14} className="text-muted-foreground" />
              </div>
              <p className="font-display text-xl text-foreground">{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-display text-lg">Net Contribution by Salesperson</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Bar dataKey="Net ($)" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Commission ($)" fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} opacity={0.4} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Per-salesperson Table */}
      <Card className="border-border">
        <CardContent className="p-0">
          {data.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">No sales data available</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Salesperson</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Net Contribution</TableHead>
                    <TableHead>Customers</TableHead>
                    <TableHead>Repeat %</TableHead>
                    <TableHead>AOV</TableHead>
                    <TableHead>Est. LTV</TableHead>
                    <TableHead>Orders</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((d) => (
                    <TableRow key={d.user_id}>
                      <TableCell className="font-medium">{d.name}</TableCell>
                      <TableCell>{fmt(d.revenue)}</TableCell>
                      <TableCell className="text-muted-foreground">{fmt(d.commission)}</TableCell>
                      <TableCell className="font-medium text-primary">{fmt(d.netContribution)}</TableCell>
                      <TableCell>{d.customers}</TableCell>
                      <TableCell>
                        <Badge variant={d.repeatRate >= 30 ? "default" : "secondary"} className="text-xs">
                          {d.repeatRate}%
                        </Badge>
                      </TableCell>
                      <TableCell>{fmt(d.aov)}</TableCell>
                      <TableCell>{fmt(d.estimatedLtv)}</TableCell>
                      <TableCell>{d.orderCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesPerformanceReport;
